import { useMemo, useState, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Dialog,
  AppBar as MuiAppBar,
  Toolbar,
  IconButton,
  Button,
  Tooltip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

import { MaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import CloseIcon from '@mui/icons-material/Close';

import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle';
import StatusBadge from '@/shared/components/StatusBadge';
import DateTimeCell from '@/shared/components/DateTimeCell';
import { getGameStatusColor } from '@/shared/utils/getGameStatusColor';
import { ROW_ACTIONS_SX, TOOLBAR_ACTIONS_SX } from '@/shared/constants/tableIconButtons';
import { cx } from '@/styles/muiTheme';
import AppPage from '../../../shared/components/PageComponents/AppPage';
import GamesFormV2, {
  DEFAULT_VALUES,
  FormValues as GamesFormValues,
} from '@/features/games/components/GamesFormV2';
import GameEditionForm from '@/features/games/components/GameEditionForm';
import GameMatchup from '@/shared/components/GameMatchup';

import { TransitionProps } from '@mui/material/transitions';
import { Slide } from '@mui/material';
import { forwardRef } from 'react';

import { GameModel } from '@/features/games/types';
import useUpdateGameDetails, { UpdateGameDetails } from '@/features/games/hooks/updateGameDetailsMutation';
import { PAGE_SIZE, useGamesQuery } from '@/features/games/hooks/useGameQuery';
import { useSnackbar } from 'notistack';
import { get } from '@truefit/http-utils';
import useConfirmationDialogContext from '@/shared/hooks/useConfirmationDialogContext';
import { Edit, OndemandVideo, ContentCopy } from '@mui/icons-material';
import GameToggle from './GameToggle';
import { mkConfig, generateCsv, download } from 'export-to-csv'; //or use your library of choice here
import { useCopyToSimuliveMutation } from '@/features/games/hooks/useCopyToSimuliveMutation';
import PageHeader from '@/shared/components/PageHeader';

// Alternative approach using only Intl (without date-fns)
function formatWithIntl(dateTimeStr: any, timezone: any) {
  const date = new Date(dateTimeStr);
  const browserLocale = navigator.language || 'en-US';

  // Get browser's timezone if none provided
  const effectiveTimezone = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;

  return new Intl.DateTimeFormat(browserLocale, {
    dateStyle: 'full',
    timeStyle: 'long',
    timeZone: effectiveTimezone,
  }).format(date);
}

// Transition component (same as before)
const Transition = forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement<unknown>;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// Utility function to remove null/undefined values
function removeNullOrUndefined(obj: GamesFormValues): GamesFormValues {
  return Object.entries(obj)
    .filter(([, value]) => value !== null && value !== undefined)
    .reduce(
      (acc, [key, value]) => {
        acc[key] = value;
        return acc;
      },
      {} as any,
    ) as GamesFormValues;
}

const csvConfig = mkConfig({
  fieldSeparator: ',',
  decimalSeparator: '.',
  filename: 'games',
  useKeysAsHeaders: true,
});

const flattenData = (data: GameModel[]) => {
  return data.map((game) => {
    return {
      id: game.id,
      name: `${game.awayTeam.name}(${game.awayTeam.abbreviation}) @ ${game.homeTeam.name} (${game.homeTeam.abbreviation})`,
      startTimestamp: formatWithIntl(game.startTimestamp, game.venue.timezone),
      venue: game.venue.name,
      status: game.status,
      available: game.available,
    };
  });
};

const ManageGamesPage = () => {
  const setConfirmationDialog = useConfirmationDialogContext();

  const [formValues, setFormValues] = useState<GamesFormValues>(DEFAULT_VALUES);
  const [showAll, setShowAll] = useState(false);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
  });

  const queryFormValues = showAll ? { ...formValues, available: false } : formValues;
  const gamesQuery = useGamesQuery(queryFormValues, paginationModel.page, paginationModel.pageSize);
  const snackBar = useSnackbar();

  const isLoading = gamesQuery.isLoading || gamesQuery.isRefetching;

  const total = useMemo(() => gamesQuery.data?.total, [gamesQuery.data]);
  const allGames = useMemo(() => gamesQuery.data?.results, [gamesQuery.data]);

  const title = 'Game Management';

  useDocumentTitle(title);

  const handleFilterAndSortChange = useCallback((values: GamesFormValues) => {
    setFormValues(removeNullOrUndefined(values));
  }, []);

  const handleExportData = useCallback(() => {
    if (!gamesQuery.data?.results) return;
    const flattenedData = flattenData(gamesQuery.data?.results);
    const csv = generateCsv(csvConfig)(flattenedData);
    download(csvConfig)(csv);
  }, [gamesQuery.data?.results]);

  // Define columns for Material React Table
  const columns = useMemo<MRT_ColumnDef<GameModel>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'ID',
        size: 90,
        // A surrogate key is a reference, not content — muted so it stops
        // competing with the matchup for attention.
        muiTableBodyCellProps: { sx: { color: cx.text.muted } },
      },
      {
        accessorKey: 'name',
        header: 'Title',
        Cell: ({ row }) => <GameMatchup game={row.original} />,
      },
      {
        accessorKey: 'startTimestamp',
        header: 'Start Time',
        size: 200,
        Cell: ({ row }) => <DateTimeCell value={row.original.startTimestamp} />,
      },
      {
        accessorKey: 'venue.name',
        header: 'Location',
      },
      {
        accessorKey: 'status',
        header: 'Status',
        Cell: ({ row }) => (
          <StatusBadge
            label={row.original.status}
            color={getGameStatusColor(row.original.status)}
            pulse={row.original.status === 'live'}
          />
        ),
      },
    ],
    [],
  );

  const [open, setOpen] = useState(false);
  const [editingGame, setEditingGame] = useState<GameModel | null>(null);
  const [loadingGame, setLoadingGame] = useState(false);

  const copyToSimuliveMutation = useCopyToSimuliveMutation();

  const handleClose = () => {
    setOpen(false);
  };
  
  const handleEditGame = useCallback(async (game: GameModel) => {
    setLoadingGame(true);
    setOpen(true);
    try {
      const { data } = await get<GameModel>(`/v1.0/games/${game.uniqueId}`);
      setEditingGame(data);
      setUpdatedDetails(data);
    } catch (error) {
      console.error('Failed to load game details:', error);
      snackBar.enqueueSnackbar('Failed to load game details', { variant: 'error' });
      setOpen(false);
    } finally {
      setLoadingGame(false);
    }
  }, [snackBar]);

  const updateGameDetails = useUpdateGameDetails();

  const [updatedDetails, setUpdatedDetails] = useState<GameModel | null>(null);

  const handleSaveGameDetails = useCallback(async () => {
    if (!updatedDetails) return;
    
    try {
      const payload: UpdateGameDetails = {
        uniqueId: updatedDetails.uniqueId,
        version: updatedDetails.version ?? 1,
      };
      
      if ('status' in updatedDetails && updatedDetails.status) payload.status = updatedDetails.status as any;
      if ('startTimestamp' in updatedDetails && updatedDetails.startTimestamp) payload.startTimestamp = updatedDetails.startTimestamp.toString();
      if ('endTimestamp' in updatedDetails && updatedDetails.endTimestamp) payload.endTimestamp = updatedDetails.endTimestamp.toString();
      if ('enableTracking' in updatedDetails) payload.enableTracking = updatedDetails.enableTracking;
      if ('trackingOffset' in updatedDetails) payload.trackingOffset = updatedDetails.trackingOffset;
      if ('trackingId' in updatedDetails) payload.trackingId = updatedDetails.trackingId;
      if ('enableAudio' in updatedDetails) payload.enableAudio = updatedDetails.enableAudio;
      if ('audioOffset' in updatedDetails) payload.audioOffset = updatedDetails.audioOffset;
      if ('streamId' in updatedDetails) payload.streamId = updatedDetails.streamId;
      if ('homeTeamColor' in updatedDetails) payload.homeTeamColor = updatedDetails.homeTeamColor;
      if ('awayTeamColor' in updatedDetails) payload.awayTeamColor = updatedDetails.awayTeamColor;
      if ('available' in updatedDetails) payload.available = updatedDetails.available;
      if ('gameCameras' in updatedDetails && updatedDetails.gameCameras) {
        payload.gameCameras = updatedDetails.gameCameras.map((gameCamera) => ({
          uniqueId: gameCamera.uniqueId,
          reingestRequired: gameCamera.reingestRequired ?? false,
          reingestStreamId: gameCamera.reingestStreamId ?? null,
        }));
      }
      
      const response = await updateGameDetails.mutateAsync(payload);
      const updatedGame = response.data;
      
      const mergedGame = {
        ...updatedDetails,
        ...updatedGame,
        homeTeam: updatedDetails.homeTeam,
        awayTeam: updatedDetails.awayTeam,
        venue: updatedDetails.venue,
        season: updatedDetails.season,
      };
      
      setEditingGame(mergedGame);
      setUpdatedDetails(mergedGame);
      
      await gamesQuery.refetch();
      snackBar.enqueueSnackbar('Game details saved successfully.', { variant: 'success' });
    } catch (e) {
      console.error(e);

      const error = e as any;
      if (error.code === 'P2002') {
        const fieldName = error.fields && error.fields[0] ? error.fields[0] : 'field';
        snackBar.enqueueSnackbar(`A game with this ${fieldName} already exists.`, {
          variant: 'error',
        });
      } else if (error.code === 'P2025') {
        snackBar.enqueueSnackbar('Game not found. It may have been deleted.', { variant: 'error' });
      } else if (error.status === 409) {
        snackBar.enqueueSnackbar(error.error || 'Conflict with existing data.', {
          variant: 'error',
        });
        await gamesQuery.refetch();
      } else if (error.status === 400) {
        snackBar.enqueueSnackbar(error.error || 'Invalid data provided.', { variant: 'error' });
      } else {
        snackBar.enqueueSnackbar('An error occurred while saving game details', {
          //@ts-expect-error
          variant: 'errorWithDetails',
          details: error.message,
        });
      }
      throw e;
    }
  }, [updateGameDetails, updatedDetails, gamesQuery, snackBar, setEditingGame, setUpdatedDetails]);

  const onSaveGame = useCallback(() => {
    setConfirmationDialog({
      confirmButtonStyle: 'error',
      title: `Save Game`,
      message: <Typography>Saving changes to this game will update the game details.</Typography>,
      confirmText: `Confirm`,
      cancelText: 'Cancel',
      onConfirm: async () => await handleSaveGameDetails(),
    });
  }, [setConfirmationDialog, handleSaveGameDetails]);

  const toggleGame = useCallback(
    async (available: boolean, game: GameModel) => {
      if (game) {
        try {
          await updateGameDetails.mutateAsync({
            uniqueId: game.uniqueId,
            available,
            version: game.version ?? 1,
          });

          await gamesQuery.refetch();

          snackBar.enqueueSnackbar('Game availability updated successfully.', {
            variant: 'success',
          });
        } catch (e) {
          console.error(e);
          snackBar.enqueueSnackbar('An error occurred while updating game availability.', {
            variant: 'error',
          });
        }
      }
    },
    [updateGameDetails, gamesQuery, snackBar],
  );

  const handleCopyToSimulive = useCallback(
    async (game: GameModel) => {
      try {
        await copyToSimuliveMutation.mutateAsync(game.uniqueId);
        snackBar.enqueueSnackbar('Simulive copy created successfully. It will appear on the Simulive page.', {
          variant: 'success',
        });
      } catch (e) {
        console.error(e);
        snackBar.enqueueSnackbar('An error occurred while creating simulive copy.', {
          variant: 'error',
        });
      }
    },
    [copyToSimuliveMutation, snackBar],
  );

  return (
    <AppPage title={title} requiredPermissions={['menu:admin:games']}>
      <Container maxWidth="xl" sx={{ marginTop: 4, marginBottom: 4 }}>
        <PageHeader title="Manage Games" subtitle="Enable / Disable Games, edit games etc." />

        <GamesFormV2 onChange={handleFilterAndSortChange} defaultDate={new Date()} />

        {total !== undefined ? (
          <Typography variant="h6" sx={{ marginTop: 2 }}>
            {total === 0 ? 'No games found.' : total > 1 ? `${total} Games` : `${total} Game`}
          </Typography>
        ) : null}

        {gamesQuery.isError ? (
          <Typography color="error" sx={{ textAlign: 'center' }}>
            An error occurred while fetching games.
          </Typography>
        ) : null}

        {/* Breathing room on both sides: the games count sits above and the
            table's own toolbar directly below, so at mt: 1 this row was pinched
            between them. */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            mt: 3,
            mb: 1.5,
          }}
        >
          <FormControlLabel
            control={<Switch checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />}
            label={<Typography variant="body2">Show unavailable games</Typography>}
          />
        </Box>

        <Box sx={{ height: 600, marginTop: 1 }}>
          <MaterialReactTable
            key={`${formValues.leagueId}-${formValues.seasonId}-${paginationModel.page}`}
            columns={columns}
            data={allGames || []}
            enablePagination
            manualPagination
            rowCount={total || 0}
            muiTopToolbarProps={{ sx: TOOLBAR_ACTIONS_SX }}
            state={{
              isLoading,
              pagination: {
                pageIndex: paginationModel.page,
                pageSize: paginationModel.pageSize,
              },
            }}
            renderTopToolbarCustomActions={() => (
              <Box
                sx={{
                  display: 'flex',
                  gap: '16px',
                  padding: '8px',
                  flexWrap: 'wrap',
                }}
              >
                <Button
                  //export all data that is currently in the table (ignore pagination, sorting, filtering, etc.)
                  onClick={handleExportData}
                  startIcon={<FileDownloadIcon />}
                >
                  Export All Data
                </Button>
              </Box>
            )}
            // Add this property to style disabled game rows with reddish background
            muiTableBodyRowProps={({ row }) => ({
              sx: {
                backgroundColor: !row.original.available ? 'rgba(156, 0, 21,0.04)' : undefined,
                '&:hover': {
                  backgroundColor: !row.original.available ? 'rgba(156, 0, 21,0.14)' : undefined,
                },
              },
            })}
            onPaginationChange={(updater) => {
              const newPagination =
                typeof updater === 'function'
                  ? updater({ pageIndex: paginationModel.page, pageSize: paginationModel.pageSize })
                  : updater;
              setPaginationModel({
                page: newPagination.pageIndex,
                pageSize: newPagination.pageSize,
              });
              // Additional server-side pagination logic can be added here
            }}
            positionActionsColumn="last"
            enableRowActions
            renderRowActions={({ row }) => (
              <Box sx={ROW_ACTIONS_SX}>
                <GameToggle game={row.original} onToggle={toggleGame} />
                {/* Separates the availability toggle, which mutates the row, from
                    the navigation/copy actions that follow it. */}
                <Box
                  sx={{ width: '1px', height: 20, mx: '4px', backgroundColor: cx.bg.elevated2 }}
                />
                <Tooltip title="Create SIMLIVE Copy" arrow>
                  <IconButton
                    onClick={() => handleCopyToSimulive(row.original as GameModel)}
                    disabled={copyToSimuliveMutation.isPending}
                    size="small"
                  >
                    <ContentCopy />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit Game" arrow>
                  <IconButton
                    onClick={() => handleEditGame(row.original as GameModel)}
                    disabled={loadingGame}
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>
                <Tooltip title="View Game" arrow>
                  <IconButton
                    onClick={() => {
                      window.open(`/games/${row.original?.uniqueId}`, '_blank');
                    }}
                  >
                    <OndemandVideo />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          />
        </Box>
      </Container>

      <Dialog open={open} onClose={handleClose} fullScreen TransitionComponent={Transition}>
        <MuiAppBar sx={{ position: 'relative' }}>
          <Toolbar>
            <IconButton edge="start" color="inherit" onClick={handleClose} aria-label="close">
              <CloseIcon />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              Edit Game
            </Typography>
            <Button
              variant="contained"
              autoFocus
              onClick={() => {
                window.open(`/games/${editingGame?.uniqueId}`, '_blank');
              }}
              sx={{ marginRight: 2 }}
            >
              View Game
            </Button>
            <Button variant="contained" autoFocus onClick={onSaveGame}>
              Save
            </Button>
          </Toolbar>
        </MuiAppBar>
        {editingGame && (
          <GameEditionForm
            gameDetails={editingGame as any}
            handleGameDataChange={(gameDetails) => setUpdatedDetails(gameDetails as any)}
            handleClose={handleClose}
          />
        )}
      </Dialog>
    </AppPage>
  );
};

export default ManageGamesPage;
